/**
 * กฎการ validate ปุ่ม "บันทึก" ของ Portal form
 * แยกเป็นฟังก์ชันเล็ก ๆ ตามหัวข้อ เพื่อให้อ่านแล้วรู้ทันทีว่าแต่ละกฎเช็คอะไร
 * Logic ทั้งหมดเดิมซ้ำกันเป๊ะทั้งใน CreatePortal.jsx และ EditPortal.jsx
 * (ต่างกันแค่ชื่อตัวแปร loginmethod / editloginmethod)
 */

// formType บางแบบไม่บังคับต้องเลือกวิธีเข้าสู่ระบบ (general-lms ใช้ ThaID แบบ fix)
const isLoginMethodExempt = (formType) => formType === "general-lms";

// formType บางแบบไม่บังคับต้องมีข้อมูลใน retrieve เลย
const isRetrieveExempt = (formType) =>
    formType === "general" ||
    formType === "general-lms" ||
    formType === "connect-mail" ||
    formType === "corporation-mail";

// general-mail ไม่บังคับต้อง "เลือก" retrieve source แต่ยังต้องเป็น array
const isRetrieveArrayLengthExempt = (formType) =>
    isRetrieveExempt(formType) || formType === "general-mail";

const hasRetrieveSource = (retrieve, sourceName) =>
    Array.isArray(retrieve) && Boolean(retrieve.find((item) => item.from === sourceName));

/** ชื่อฟอร์มและประเภทฟอร์มต้องกรอกครบ */
const hasBasicFields = ({ formName, formType }) =>
    formName !== '' && formType !== '';

/** ต้องเลือกวิธีเข้าสู่ระบบอย่างน้อย 1 วิธี (ยกเว้น general-lms) */
const hasValidLoginMethod = ({ formType, loginMethod }) => {
    if (isLoginMethodExempt(formType)) return true;
    return loginMethod !== '' && loginMethod.length > 0;
};

/** ต้องเลือกวิธีดึงข้อมูลส่วนบุคคลอย่างน้อย 1 วิธี (ยกเว้นบาง formType) */
const hasValidRetrieveSelection = ({ formType, retrieve }) => {
    if (retrieve === '' && !isRetrieveExempt(formType)) return false;
    if (Array.isArray(retrieve) && retrieve.length === 0 && !isRetrieveArrayLengthExempt(formType)) return false;
    return true;
};

/** ถ้าเลือกดึงข้อมูลจากแหล่งที่ต้องกรอก URL/Token เพิ่ม ต้องกรอกให้ครบ */
const hasValidRetrieveSourceUrls = ({
    formType, retrieve, link, token, azureUrl, googleUrl, facebookUrl,
    useClientSecret, clientKey, secretKey,
}) => {
    if (isLoginMethodExempt(formType)) return true;

    if (hasRetrieveSource(retrieve, "API")) {
        if (link === '') return false;
        if (useClientSecret) {
            if (clientKey === '' || secretKey === '') return false;
        } else {
            if (token === '') return false;
        }
    }
    if (hasRetrieveSource(retrieve, "Azure") && azureUrl === '') return false;
    if (hasRetrieveSource(retrieve, "Google") && googleUrl === '') return false;
    if (hasRetrieveSource(retrieve, "Facebook") && facebookUrl === '') return false;

    return true;
};

/** redirect_URL และ Client_ID เป็นค่าบังคับเสมอ ไม่ขึ้นกับ formType */
const hasValidRedirectSettings = ({ redirectUrl, clientId }) =>
    redirectUrl !== '' && clientId !== '';

/**
 * สถานะการกรอกข้อมูล sub-field ของ OneID สำหรับ corporation-pre-register
 * ใช้ทั้งกับ validate ปุ่มบันทึก และแสดงข้อความ Alert ในหน้าฟอร์ม (เดิมสองที่นี้
 * เขียน .some() แยกกันคนละจุดและมี bug เดียวกัน — ดูคำอธิบายด้านล่าง)
 *
 * สำคัญ: ต้องเช็คกับ object ของ OneID โดยเฉพาะเท่านั้น (ไม่ใช่ .some() วนทั้ง
 * retrieve array) เพราะ retrieve อาจมี object ของ API/Azure/Google/Facebook
 * ปนอยู่ด้วย ซึ่ง object พวกนั้นไม่มี key corporation_Id/cid/email เลย ถ้าใช้
 * .some() วนทั้ง array จะเจอ object อื่นที่ "ไม่มี field ครบ" ก่อนแล้วตอบผิดว่า
 * ยังกรอกไม่ครบ ทั้งที่ OneID กรอกครบแล้ว (bug เดิมที่ทำให้เลือกหลายวิธีพร้อมกัน
 * เช่น API + OneID + Azure แล้วทั้งปุ่มบันทึกและข้อความ Alert ขึ้นผิด)
 *
 * @returns {"missing"|"incomplete"|"complete"}
 *   missing    - ยังไม่ได้เลือก OneID หรือเลือกแล้วแต่ไม่ได้ติ๊ก sub-field ใดเลย
 *   incomplete - ติ๊กแค่ "เลขสมาชิก" อย่างเดียว ต้องติ๊ก cid หรือ email เพิ่ม
 *   complete   - กรอกครบถูกต้อง
 */
export const getCorporationRetrieveStatus = (retrieve) => {
    const oneIdEntry = Array.isArray(retrieve) && retrieve.find((obj) => obj.from === "OneID");
    if (!oneIdEntry) return "missing";

    const hasNoSubFieldSelected =
        oneIdEntry.corporation_Id == null && oneIdEntry.cid == null && oneIdEntry.email == null;
    if (hasNoSubFieldSelected) return "missing";

    const hasCorporationIdOnly =
        oneIdEntry.corporation_Id != null && oneIdEntry.cid == null && oneIdEntry.email == null;
    if (hasCorporationIdOnly) return "incomplete";

    return "complete";
};

const hasValidCorporationRetrieve = ({ formType, retrieve }) => {
    if (formType !== "corporation-pre-register") return true;
    return getCorporationRetrieveStatus(retrieve) === "complete";
};

const PORTAL_FORM_RULES = [
    hasBasicFields,
    hasValidLoginMethod,
    hasValidRetrieveSelection,
    hasValidRetrieveSourceUrls,
    hasValidRedirectSettings,
    hasValidCorporationRetrieve,
];

/**
 * @param {Object} fields
 * @param {string} fields.formName
 * @param {string} fields.formType
 * @param {Array|string} fields.loginMethod - loginmethod (Create) หรือ editloginmethod (Edit)
 * @param {Array|string} fields.retrieve
 * @param {string} fields.link
 * @param {string} fields.token
 * @param {string} fields.redirectUrl
 * @param {string} fields.clientId
 * @param {string} fields.azureUrl
 * @param {string} fields.googleUrl
 * @param {string} fields.facebookUrl
 * @returns {boolean} true = ฟอร์มกรอกครบถูกต้อง พร้อมบันทึก
 */
export const isPortalFormValid = (fields) =>
    PORTAL_FORM_RULES.every((rule) => rule(fields));

/**
 * ใช้กับ prop `disabled` ของปุ่มบันทึกโดยตรง (เป็นค่าตรงข้ามกับ isPortalFormValid)
 * @returns {boolean} true = ควร disabled ปุ่มบันทึก
 */
export const isPortalSaveDisabled = (fields) => !isPortalFormValid(fields);